import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { addNote, getReview, hideReview, publishReview, removeReview } from '../../api/adminReviews';
import { useToast } from '../../components/ToastContext';

const statusTone = (status) => {
  const value = (status || '').toLowerCase();
  if (value === 'published') return 'bg-emerald-50 text-emerald-700 ring-emerald-100';
  if (value === 'pending') return 'bg-amber-50 text-amber-700 ring-amber-100';
  if (value === 'hidden') return 'bg-sky-50 text-sky-700 ring-sky-100';
  if (value === 'removed') return 'bg-rose-50 text-rose-700 ring-rose-100';
  return 'bg-slate-100 text-slate-700 ring-slate-200';
};

const RatingRow = ({ label, value }) => (
  <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
    <span className="text-slate-700">{label}</span>
    <span className="font-semibold text-slate-900">{value ? `${value} / 5` : '—'}</span>
  </div>
);

const AdminReviewDetail = ({ token }) => {
  const { id } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getReview(token, id);
      setData(res);
      setNote(res.review?.admin_note || '');
    } catch (err) {
      setError(err.message || 'Failed to load review');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const perform = async (action) => {
    const reason = action === 'publish' ? '' : window.prompt('Reason (optional)') || '';
    try {
      if (action === 'publish') await publishReview(token, id, { reason });
      if (action === 'hide') await hideReview(token, id, { reason });
      if (action === 'remove') await removeReview(token, id, { reason });
      toast.success('Status updated');
      await load();
    } catch (err) {
      toast.error(err.message || 'Update failed');
    }
  };

  const saveNote = async () => {
    try {
      await addNote(token, id, { admin_note: note });
      toast.success('Note saved');
    } catch (err) {
      toast.error(err.message || 'Save failed');
    }
  };

  if (error) {
    return (
      <div className="admin-card rounded-lg bg-white p-4 shadow-sm ring-1 ring-rose-200">
        <p className="text-rose-700">{error}</p>
        <button
          type="button"
          onClick={() => navigate('/admin/reviews')}
          className="mt-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Back to reviews
        </button>
      </div>
    );
  }

  if (loading || !data) {
    return <div className="admin-card rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">Loading...</div>;
  }

  const review = data.review || {};
  const events = data.events || [];

  return (
    <div className="admin-card space-y-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-start justify-between border-b border-slate-200 pb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-emerald-700">Review #{review.id}</p>
          <h1 className="admin-page-title text-xl font-bold text-slate-900">{review.property_title || 'Property'}</h1>
          <p className="text-sm text-slate-600">
            Host: {review.host_name || 'Host'} · Guest: {review.guest_name || 'Guest'}
          </p>
          <p className="text-xs text-slate-500">Booking: {review.booking_code || 'N/A'}</p>
        </div>
        <div className="space-y-2 text-right">
          <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${statusTone(review.status)}`}>
            {review.status}
          </span>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => perform('publish')}
              className="rounded-lg border border-emerald-200 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
            >
              Publish
            </button>
            <button
              type="button"
              onClick={() => perform('hide')}
              className="rounded-lg border border-sky-200 px-3 py-2 text-xs font-semibold text-sky-700 hover:bg-sky-50"
            >
              Hide
            </button>
            <button
              type="button"
              onClick={() => perform('remove')}
              className="rounded-lg border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-50"
            >
              Remove
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-3 lg:col-span-2">
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Overall rating</p>
                <p className="text-2xl font-bold text-slate-900">{review.rating_overall} / 5</p>
              </div>
              <div className="text-xs text-slate-500">{new Date(review.created_at).toLocaleString()}</div>
            </div>
            <p className="mt-3 text-sm text-slate-800 whitespace-pre-line">{review.comment}</p>
            {review.private_note_to_host && (
              <div className="mt-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800 ring-1 ring-amber-100">
                Private note: {review.private_note_to_host}
              </div>
            )}
            {review.host_response && (
              <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-800 ring-1 ring-slate-200">
                <p className="text-xs uppercase tracking-wide text-slate-500">Host response</p>
                <p>{review.host_response}</p>
                <p className="mt-1 text-xs text-slate-500">
                  Responded at {new Date(review.host_responded_at || review.updated_at).toLocaleString()}
                </p>
              </div>
            )}
          </div>

          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <RatingRow label="Cleanliness" value={review.rating_cleanliness} />
            <RatingRow label="Accuracy" value={review.rating_accuracy} />
            <RatingRow label="Check-in" value={review.rating_checkin} />
            <RatingRow label="Communication" value={review.rating_communication} />
            <RatingRow label="Location" value={review.rating_location} />
            <RatingRow label="Value" value={review.rating_value} />
          </div>
        </div>

        <div className="space-y-3">
          <div className="rounded-lg border border-slate-200 p-3 text-sm">
            <p className="text-sm font-semibold text-slate-900">Booking</p>
            <p className="text-slate-700">{review.booking_code || 'N/A'}</p>
            <p className="text-xs text-slate-500">
              {review.check_in_date} - {review.check_out_date}
            </p>
            <p className="mt-2 text-sm font-semibold text-slate-900">Admin note</p>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
            <button
              type="button"
              onClick={saveNote}
              className="mt-2 w-full rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            >
              Save note
            </button>
          </div>

          <div className="rounded-lg border border-slate-200 p-3">
            <p className="text-sm font-semibold text-slate-900">Events</p>
            <div className="mt-2 space-y-2 text-sm">
              {events.map((evt) => (
                <div key={evt.id} className="rounded border border-slate-100 bg-slate-50 px-3 py-2">
                  <p className="font-semibold text-slate-900">{evt.event_type}</p>
                  <p className="text-xs text-slate-500">{new Date(evt.created_at).toLocaleString()}</p>
                  {evt.metadata && Object.keys(evt.metadata || {}).length > 0 && (
                    <pre className="mt-1 whitespace-pre-wrap text-xs text-slate-600">{JSON.stringify(evt.metadata)}</pre>
                  )}
                </div>
              ))}
              {!events.length && <p className="text-xs text-slate-500">No events yet.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminReviewDetail;
